/**
 * Number/date formatters.
 * Semua nilai numerik di-coerce (API sering kirim string).
 */

function toNum(n: unknown): number | null {
  if (n === null || n === undefined || n === '') return null;
  if (typeof n === 'number') return Number.isFinite(n) ? n : null;
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
}

export const fmt = {
  usd(n: number | string | null | undefined, decimals = 2): string {
    const v = toNum(n);
    if (v === null) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(v);
  },

  number(n: number | string | null | undefined, decimals = 0): string {
    const v = toNum(n);
    if (v === null) return '—';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(v);
  },

  percent(n: number | string | null | undefined, decimals = 2): string {
    const v = toNum(n);
    if (v === null) return '—';
    return `${v.toFixed(decimals)}%`;
  },

  score(n: number | string | null | undefined, decimals = 1): string {
    const v = toNum(n);
    if (v === null) return '—';
    return v.toFixed(decimals);
  },

  /** Format: "Jumat, 24 Juli" — tanpa jam, untuk settlement */
  dateDay(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('id-ID', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  },

  date(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  },

  dateFull(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },

  countdown(iso: string | null | undefined): string {
    if (!iso) return '—';
    const ms = new Date(iso).getTime() - Date.now();
    if (Number.isNaN(ms)) return '—';
    if (ms < 0) return 'Settled';
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days < 1) return '< 1d';
    return `${days}d`;
  },

  idr(n: number | string | null | undefined, decimals = 0): string {
    const v = toNum(n);
    if (v === null) return '—';
    return `Rp ${new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(v)}`;
  },

  /**
   * Compact for mobile cards — avoids overflow on long prices/IDR.
   * 1234 → 1.23K · 1_200_000 → 1.2M · 12_500_000_000 → 12.5B
   */
  compact(n: number | string | null | undefined, decimals = 2): string {
    const v = toNum(n);
    if (v === null) return '—';
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(decimals)}T`;
    if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(decimals)}B`;
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(decimals)}M`;
    if (abs >= 1e4) return `${sign}${(abs / 1e3).toFixed(decimals)}K`;
    if (abs >= 100) return `${sign}${abs.toFixed(0)}`;
    return `${sign}${abs.toFixed(Math.min(decimals, 2))}`;
  },

  /** Rp compact: 1.2jt / 850rb / 12.5M */
  idrCompact(n: number | string | null | undefined): string {
    const v = toNum(n);
    if (v === null) return '—';
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 1e12) return `${sign}Rp ${(abs / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `${sign}Rp ${(abs / 1e9).toFixed(2)}M`;
    if (abs >= 1e6) return `${sign}Rp ${(abs / 1e6).toFixed(1)}jt`;
    if (abs >= 1e3) return `${sign}Rp ${(abs / 1e3).toFixed(0)}rb`;
    return `${sign}Rp ${Math.round(abs)}`;
  },

  /** BTC price friendly on mobile cards */
  price(n: number | string | null | undefined): string {
    const v = toNum(n);
    if (v === null) return '—';
    if (v >= 1000) return fmt.number(v, 0);
    if (v >= 1) return fmt.number(v, 2);
    return fmt.number(v, 4);
  },
};
